import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  TrendingUp,
  AlertTriangle,
  Calendar,
  Home,
  Loader2,
  RefreshCw,
  Plus,
  Send,
  FileText,
  Gavel,
  Clock,
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  served: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  disputed: 'bg-red-100 text-red-700',
  applied: 'bg-purple-100 text-purple-700',
  court_scheduled: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700'
};

export default function RentEvictionDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('rent-increase');
  const [loading, setLoading] = useState(false);
  
  // Tenancies for dropdown
  const [tenancies, setTenancies] = useState([]);
  
  // Rent Increase State
  const [rentIncreases, setRentIncreases] = useState([]);
  const [showCreateIncrease, setShowCreateIncrease] = useState(false);
  const [newIncrease, setNewIncrease] = useState({
    tenancy_id: '',
    proposed_rent: '',
    effective_date: ''
  });
  const [creatingIncrease, setCreatingIncrease] = useState(false);
  
  // Eviction State
  const [evictions, setEvictions] = useState([]);
  const [showCreateEviction, setShowCreateEviction] = useState(false);
  const [newEviction, setNewEviction] = useState({
    tenancy_id: '',
    notice_type: 'section_21',
    grounds: []
  });
  const [creatingEviction, setCreatingEviction] = useState(false);
  
  // Section 8 grounds
  const SECTION_8_GROUNDS = [
    { value: 'ground_8', label: 'Ground 8: 8+ weeks rent arrears' },
    { value: 'ground_10', label: 'Ground 10: Some rent arrears' },
    { value: 'ground_11', label: 'Ground 11: Persistent late payment' },
    { value: 'ground_12', label: 'Ground 12: Breach of agreement' },
    { value: 'ground_14', label: 'Ground 14: Anti-social behaviour' },
    { value: 'ground_17', label: 'Ground 17: False statement' }
  ];
  
  // Fetch tenancies
  const fetchTenancies = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/tenancies/landlord`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTenancies(data.tenancies || data || []);
      }
    } catch (error) {
      console.error('Error fetching tenancies:', error);
    }
  }, [token]);
  
  // Fetch rent increases
  const fetchRentIncreases = useCallback(async () => {
    // This would need a new endpoint - for now mock
    setRentIncreases([]);
  }, []);
  
  // Fetch evictions
  const fetchEvictions = useCallback(async () => {
    // This would need a new endpoint - for now mock
    setEvictions([]);
  }, []);
  
  useEffect(() => {
    fetchTenancies();
    fetchRentIncreases();
    fetchEvictions();
  }, [fetchTenancies, fetchRentIncreases, fetchEvictions]);
  
  // Create rent increase
  const handleCreateIncrease = async () => {
    if (!newIncrease.tenancy_id || !newIncrease.proposed_rent || !newIncrease.effective_date) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setCreatingIncrease(true);
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/rent-increase/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newIncrease)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Rent increase created!');
        setShowCreateIncrease(false);
        setNewIncrease({ tenancy_id: '', proposed_rent: '', effective_date: '' });
        // Add to list
        setRentIncreases(prev => [data, ...prev]);
      } else {
        toast.error(data.detail || 'Failed to create rent increase');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setCreatingIncrease(false);
    }
  };
  
  // Serve rent increase notice
  const handleServeIncrease = async (increaseId) => {
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/rent-increase/serve/${increaseId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Notice served to tenant');
        fetchRentIncreases();
      } else {
        toast.error('Failed to serve notice');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };
  
  // Create eviction notice
  const handleCreateEviction = async () => {
    if (!newEviction.tenancy_id || !newEviction.notice_type) {
      toast.error('Please select a tenancy and notice type');
      return;
    }
    
    if (newEviction.notice_type === 'section_8' && newEviction.grounds.length === 0) {
      toast.error('Please select at least one ground for Section 8');
      return;
    }
    
    setCreatingEviction(true);
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/eviction/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newEviction)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Eviction notice created!');
        setShowCreateEviction(false);
        setNewEviction({ tenancy_id: '', notice_type: 'section_21', grounds: [] });
        setEvictions(prev => [data, ...prev]);
      } else {
        toast.error(data.detail || 'Failed to create eviction notice');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setCreatingEviction(false);
    }
  };
  
  // Serve eviction notice
  const handleServeEviction = async (noticeId, method) => {
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/eviction/serve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notice_id: noticeId,
          served_method: method
        })
      });
      
      if (response.ok) {
        toast.success('Notice served');
        fetchEvictions();
      } else {
        toast.error('Failed to serve notice');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };
  
  // Toggle ground selection
  const toggleGround = (ground) => {
    setNewEviction(prev => ({
      ...prev,
      grounds: prev.grounds.includes(ground)
        ? prev.grounds.filter(g => g !== ground)
        : [...prev.grounds, ground]
    }));
  };
  
  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="rent-eviction-dashboard">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Rent & Tenancy Management</h1>
            <p className="text-gray-600">Manage rent increases and tenancy notices</p>
          </div>
          <Button variant="outline" onClick={() => { fetchRentIncreases(); fetchEvictions(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* Important Notice */}
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Important Legal Notice</p>
                <p className="text-sm text-amber-700">
                  Rent increases and eviction notices must comply with current UK legislation. 
                  LetWise automatically calculates notice periods based on jurisdiction but we recommend 
                  seeking legal advice for complex cases.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="rent-increase" data-testid="rent-increase-tab">
              <TrendingUp className="w-4 h-4 mr-2" />
              Rent Increases
            </TabsTrigger>
            <TabsTrigger value="eviction" data-testid="eviction-tab">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Eviction Notices
            </TabsTrigger>
          </TabsList>
          
          {/* Rent Increase Tab */}
          <TabsContent value="rent-increase" className="mt-6 space-y-6">
            {/* Create Form */}
            {showCreateIncrease ? (
              <Card>
                <CardHeader>
                  <CardTitle>Create Rent Increase</CardTitle>
                  <CardDescription>
                    Notice periods are calculated automatically based on property jurisdiction
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Select Tenancy *</Label>
                    <select
                      value={newIncrease.tenancy_id}
                      onChange={(e) => setNewIncrease(prev => ({ ...prev, tenancy_id: e.target.value }))}
                      className="w-full border rounded-lg p-3"
                      data-testid="tenancy-select"
                    >
                      <option value="">-- Select a tenancy --</option>
                      {tenancies.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.property?.address_line_1 || t.address} - {t.tenant_name || 'Tenant'}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>New Monthly Rent (£) *</Label>
                      <Input
                        type="number"
                        value={newIncrease.proposed_rent}
                        onChange={(e) => setNewIncrease(prev => ({ ...prev, proposed_rent: e.target.value }))}
                        placeholder="e.g. 1200"
                        data-testid="proposed-rent-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Effective Date *</Label>
                      <Input
                        type="date"
                        value={newIncrease.effective_date}
                        onChange={(e) => setNewIncrease(prev => ({ ...prev, effective_date: e.target.value }))}
                        min={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                        data-testid="effective-date-input"
                      />
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-800 mb-2">Notice Period Information</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• England/Wales: 1 month (monthly tenancy) or 6 months (yearly)</li>
                      <li>• Scotland: Minimum 3 months notice</li>
                      <li>• The system will calculate the correct notice date automatically</li>
                    </ul>
                  </div>
                </CardContent>
                <CardFooter className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowCreateIncrease(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleCreateIncrease}
                    disabled={creatingIncrease}
                    data-testid="create-increase-btn"
                  >
                    {creatingIncrease ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Create Rent Increase
                  </Button>
                </CardFooter>
              </Card>
            ) : (
              <Button onClick={() => setShowCreateIncrease(true)} data-testid="new-increase-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Rent Increase
              </Button>
            )}
            
            {/* List of rent increases */}
            {rentIncreases.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <TrendingUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-600">No Rent Increases</h3>
                  <p className="text-sm text-gray-500 mt-2">
                    Create a rent increase to propose a new rent amount to your tenant.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {rentIncreases.map((ri) => (
                  <Card key={ri.increase_id || ri.id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Home className="w-5 h-5" />
                            {ri.property_address || 'Property'}
                          </CardTitle>
                          <CardDescription>
                            £{ri.current_rent} → £{ri.proposed_rent} ({ri.increase_percentage?.toFixed(1)}% increase)
                          </CardDescription>
                        </div>
                        <Badge className={STATUS_COLORS[ri.status] || 'bg-gray-100'}>
                          {ri.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Effective Date</p>
                          <p className="font-medium">{ri.effective_date}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Notice Date</p>
                          <p className="font-medium">{ri.notice_date}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Jurisdiction</p>
                          <p className="font-medium capitalize">{ri.jurisdiction}</p>
                        </div>
                      </div>
                    </CardContent>
                    {ri.status === 'draft' && (
                      <CardFooter>
                        <Button onClick={() => handleServeIncrease(ri.increase_id || ri.id)}>
                          <Send className="w-4 h-4 mr-2" />
                          Serve Notice
                        </Button>
                      </CardFooter>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* Eviction Tab */}
          <TabsContent value="eviction" className="mt-6 space-y-6">
            {/* Create Form */}
            {showCreateEviction ? (
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="text-red-700">Create Eviction Notice</CardTitle>
                  <CardDescription>
                    Please ensure you have valid grounds before proceeding
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Select Tenancy *</Label>
                    <select
                      value={newEviction.tenancy_id}
                      onChange={(e) => setNewEviction(prev => ({ ...prev, tenancy_id: e.target.value }))}
                      className="w-full border rounded-lg p-3"
                      data-testid="eviction-tenancy-select"
                    >
                      <option value="">-- Select a tenancy --</option>
                      {tenancies.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.property?.address_line_1 || t.address} - {t.tenant_name || 'Tenant'}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Notice Type *</Label>
                    <select
                      value={newEviction.notice_type}
                      onChange={(e) => setNewEviction(prev => ({ ...prev, notice_type: e.target.value, grounds: [] }))}
                      className="w-full border rounded-lg p-3"
                      data-testid="notice-type-select"
                    >
                      <option value="section_21">Section 21 (No-fault)</option>
                      <option value="section_8">Section 8 (With grounds)</option>
                    </select>
                  </div>
                  
                  {newEviction.notice_type === 'section_21' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h4 className="font-medium text-amber-800 mb-2">Section 21 Notice</h4>
                      <ul className="text-sm text-amber-700 space-y-1">
                        <li>• Requires 2 months notice (may vary)</li>
                        <li>• No grounds required</li>
                        <li>• Cannot be used during fixed term unless break clause exists</li>
                        <li>• Deposit must be protected and prescribed information given</li>
                        <li>• Property must have valid gas safety certificate and EPC</li>
                      </ul>
                    </div>
                  )}
                  
                  {newEviction.notice_type === 'section_8' && (
                    <div className="space-y-3">
                      <Label>Select Grounds *</Label>
                      <div className="grid grid-cols-1 gap-2">
                        {SECTION_8_GROUNDS.map(ground => (
                          <label 
                            key={ground.value}
                            className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                              newEviction.grounds.includes(ground.value) 
                                ? 'border-red-500 bg-red-50' 
                                : 'border-gray-200'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={newEviction.grounds.includes(ground.value)}
                              onChange={() => toggleGround(ground.value)}
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{ground.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
                <CardFooter className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowCreateEviction(false)}>
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={handleCreateEviction}
                    disabled={creatingEviction}
                    data-testid="create-eviction-btn"
                  >
                    {creatingEviction ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Create Notice
                  </Button>
                </CardFooter>
              </Card>
            ) : (
              <Button 
                variant="destructive" 
                onClick={() => setShowCreateEviction(true)}
                data-testid="new-eviction-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                New Eviction Notice
              </Button>
            )}
            
            {/* List of evictions */}
            {evictions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-600">No Eviction Notices</h3>
                  <p className="text-sm text-gray-500 mt-2">
                    No active eviction notices. We hope you never need this feature!
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {evictions.map((ev) => (
                  <Card key={ev.notice_id || ev.id} className="border-red-200">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Home className="w-5 h-5" />
                            {ev.property_address || 'Property'}
                          </CardTitle>
                          <CardDescription>
                            {ev.notice_type?.replace('_', ' ').toUpperCase()} Notice
                          </CardDescription>
                        </div>
                        <Badge className={STATUS_COLORS[ev.status] || 'bg-gray-100'}>
                          {ev.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Notice Date</p>
                          <p className="font-medium">{ev.notice_date}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Expiry Date</p>
                          <p className="font-medium">{ev.expiry_date}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Notice Period</p>
                          <p className="font-medium">{ev.notice_period_days} days</p>
                        </div>
                      </div>
                      
                      {ev.grounds && ev.grounds.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm text-gray-500 mb-2">Grounds:</p>
                          <div className="flex flex-wrap gap-2">
                            {ev.grounds.map(g => (
                              <Badge key={g} variant="outline" className="text-red-600 border-red-300">
                                {g.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {ev.court_date && (
                        <div className="mt-4 bg-amber-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-amber-800">
                            <Gavel className="w-4 h-4 inline mr-1" />
                            Court Date: {ev.court_date}
                            {ev.court_reference && ` (Ref: ${ev.court_reference})`}
                          </p>
                        </div>
                      )}
                    </CardContent>
                    {ev.status === 'draft' && (
                      <CardFooter className="flex gap-2">
                        <Button 
                          variant="outline"
                          onClick={() => handleServeEviction(ev.notice_id || ev.id, 'email')}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Serve by Email
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => handleServeEviction(ev.notice_id || ev.id, 'post')}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Serve by Post
                        </Button>
                      </CardFooter>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
